/**
 * Lo que no puede salir publicado, tapado por posición y no por suerte.
 *
 * El Nº de Trámite permite hacer gestiones a nombre del titular. Hasta ahora se
 * tapaba SI el OCR alcanzaba a leer la palabra "Trámite" al lado: si no la leía,
 * no se tapaba nada y nadie se enteraba. Eso es una apuesta, no una garantía, y
 * en el modelo 2026 —donde el trámite está en el dorso— no se habría tapado
 * nunca.
 *
 * Ubicada la tarjeta y reconocido el modelo, el trámite es un rectángulo
 * conocido y se tapa siempre. Ahí sí hay garantía. Y cuando no se dan las dos
 * condiciones, no la hay: eso se dice, no se disimula.
 */
import { Cara, ModeloDeDocumento, zonasDe } from "./catalogo";
import { Punto, aplicar, homografia } from "./geometria";
import { Rectangulo, ZonaSensible } from "./types";

/** Las cuatro esquinas de la tarjeta en la imagen: SI, SD, ID, II. */
export type EsquinasDeTarjeta = [Punto, Punto, Punto, Punto];

/** Las esquinas de una tarjeta derecha que ocupa el rectángulo dado. */
export function esquinasDeRectangulo(r: Rectangulo): EsquinasDeTarjeta {
  return [
    { x: r.x, y: r.y },
    { x: r.x + r.w, y: r.y },
    { x: r.x + r.w, y: r.y + r.h },
    { x: r.x, y: r.y + r.h },
  ];
}

/**
 * Una caja del catálogo —fracciones de la tarjeta— llevada a la imagen: la caja
 * alineada a los ejes que contiene a la zona, con la tarjeta girada o en
 * perspectiva. Contenerla es tapar un poco de más en las puntas cuando la foto
 * está girada, y nunca de menos.
 */
export function cajaEnLaImagen(caja: Rectangulo, tarjeta: EsquinasDeTarjeta): Rectangulo | null {
  const H = homografia(esquinasDeRectangulo({ x: 0, y: 0, w: 1, h: 1 }), tarjeta);
  if (!H) return null;
  const q = esquinasDeRectangulo(caja).map((p) => aplicar(H, p));
  const xs = q.map((p) => p.x);
  const ys = q.map((p) => p.y);
  const x = Math.floor(Math.min(...xs));
  const y = Math.floor(Math.min(...ys));
  return { x, y, w: Math.ceil(Math.max(...xs)) - x, h: Math.ceil(Math.max(...ys)) - y };
}

export interface Tapado {
  /** Rectángulos a tapar, en píxeles de la imagen enderezada. */
  zonas: ZonaSensible[];
  /**
   * Si el tapado es una garantía o no. Falso cuando falta cualquiera de las dos
   * condiciones: tarjeta ubicada, y modelo con la zona MEDIDA.
   */
  garantizado: boolean;
  motivo?: "sin_tarjeta" | "sin_modelo" | "zona_sin_medir" | "el_modelo_no_declara_zonas";
}

/**
 * Qué tapar en esta cara.
 *
 * Se exige `procedencia: "medida"`. Una caja estimada tapa un rectángulo que
 * puede estar corrido, y tapar de más arruina el documento para quien lo
 * recibe: en SADAIC el frente lo lee una persona, y un rectángulo negro sobre
 * el código de barras o sobre el número de documento le saca justamente lo que
 * necesita. Ya pasó una vez.
 */
export function zonasATapar(
  modelo: ModeloDeDocumento | null,
  cara: Cara,
  /** Las esquinas de la tarjeta en la imagen que se entrega, o null si no se ubicó. */
  tarjeta: EsquinasDeTarjeta | null
): Tapado {
  if (!tarjeta) return { zonas: [], garantizado: false, motivo: "sin_tarjeta" };
  if (!modelo) return { zonas: [], garantizado: false, motivo: "sin_modelo" };

  const sensibles = zonasDe(modelo, cara).filter((z) => z.sensible);
  if (sensibles.length === 0) {
    // Que esta cara no tenga nada sensible es una respuesta válida: en el modelo
    // 2026 el frente no lleva trámite porque está en el dorso.
    return { zonas: [], garantizado: true, motivo: "el_modelo_no_declara_zonas" };
  }

  const medidas = sensibles.filter((z) => z.procedencia === "medida");
  if (medidas.length < sensibles.length) {
    return { zonas: [], garantizado: false, motivo: "zona_sin_medir" };
  }

  const zonas: ZonaSensible[] = [];
  for (const z of medidas) {
    const caja = cajaEnLaImagen(z.caja, tarjeta);
    if (!caja) return { zonas: [], garantizado: false, motivo: "sin_tarjeta" };
    zonas.push({ campo: z.campo, caja, origen: "plantilla" });
  }
  return { zonas, garantizado: true };
}

/** Une los rectángulos de plantilla con los que agregó una persona a mano. */
export function conTapadoManual(t: Tapado, manuales: Rectangulo[]): ZonaSensible[] {
  return [
    ...t.zonas,
    ...manuales.map((caja) => ({ campo: "manual", caja, origen: "manual" as const })),
  ];
}
